import * as Urql from 'urql';
import gql from 'graphql-tag';

import * as Types from '../../../types/api.generated';
import { BoostedMultipliersFragmentDoc, EntryPromotionDetailsFragmentDoc } from '../../../api/entries/query.generated';
import { MaxPicksPerTypeFragmentDoc } from '../../../api/limits-and-rules/query.generated';

export type Omit<T, K extends keyof T> = Pick<T, Exclude<keyof T, K>>;
export type ValidateEntryQueryVariables = Types.Exact<{
    entry: Types.EntryValidation;
}>;

export type ValidateEntryQuery = {
    __typename?: 'Query';
    validateEntry: {
        __typename?: 'EntryValidationOutput';
        entryRulesId?: string;
        currency: Types.Currency;
        amount: number;
        status: Types.EntryValidationStatus;
        toWin: number;
        p2pGuaranteedPrize?: number;
        p2pGuaranteedPrizeAmount?: number;
        perfectRegularMultiplier?: number;
        perfectBoostedMultiplier?: number;
        minMysteryMultiplier?: number;
        maxMysteryMultiplier?: number;
        mysteryMultipliers?: Array<number>;
        gameMode?: Types.GameMode;
        maxAmount: number;
        minAmount: number;
        minNumberOfTeams: number;
        minNumberOfPicks: number;
        maxNumberOfPicks: number;
        edgeCombos?: Array<Array<string>>;
        dynamicMultipliers?: Array<{
            __typename?: 'DynamicMultiplier';
            multiplier: number;
            numberOfPicks: number;
            winningPicks: number;
            toWin: number;
        }>;
        boostedMultipliers?: Array<{
            __typename?: 'BoostedMultiplier';
            numberOfPicks: number;
            numberOfBoostedPicks: number;
            multiplier: number;
            toWin: number;
        }>;
        maxNumberOfPicksForType?: Array<{
            __typename?: 'MaxPicksForType';
            type: Types.ProjectionType;
            maxPicks: number;
        }>;
        remaining: Array<{ __typename?: 'RemainingPickAmountType'; type: Types.ProjectionType; count: number }>;
        errors: Array<{
            __typename?: 'EntryError';
            code: number;
            key: string;
            type: Types.EntryErrorType;
            message: string;
            restrictedPlayerIdPairs?: Array<Array<string>>;
        }>;
        promotions: Array<{
            __typename?: 'EntryPromotionStatus';
            promotion: {
                __typename?: 'EntryPromotion';
                id: string;
                name: string;
                description: string;
                autoApply: boolean;
                type: Types.PromotionType;
                criteria: Array<{ __typename?: 'Criterion'; type: Types.CriterionType; value: string }>;
                metadata: Array<{ __typename?: 'DataField'; type: Types.MetadataType; value: string }>;
            };
            errors?: Array<{
                __typename?: 'EntryPromotionError';
                type?: Types.CriterionType;
                title: string;
                shortDescription: string;
                longDescription: string;
                order: number;
            }>;
        }>;
    };
};

export type ValidateEntryWithRulesQueryVariables = Types.Exact<{
    rulesIds: Array<Types.Scalars['String']['input']> | Types.Scalars['String']['input'];
    entry: Types.EntryValidation;
}>;

export type ValidateEntryWithRulesQuery = {
    __typename?: 'Query';
    validateEntryOnEntryRules: Array<{
        __typename?: 'EntryValidationOutput';
        entryRulesId?: string;
        currency: Types.Currency;
        amount: number;
        status: Types.EntryValidationStatus;
        toWin: number;
        p2pGuaranteedPrize?: number;
        p2pGuaranteedPrizeAmount?: number;
        perfectRegularMultiplier?: number;
        perfectBoostedMultiplier?: number;
        minMysteryMultiplier?: number;
        maxMysteryMultiplier?: number;
        mysteryMultipliers?: Array<number>;
        gameMode?: Types.GameMode;
        maxAmount: number;
        minAmount: number;
        minNumberOfTeams: number;
        minNumberOfPicks: number;
        maxNumberOfPicks: number;
        edgeCombos?: Array<Array<string>>;
        dynamicMultipliers?: Array<{
            __typename?: 'DynamicMultiplier';
            multiplier: number;
            numberOfPicks: number;
            winningPicks: number;
            toWin: number;
        }>;
        boostedMultipliers?: Array<{
            __typename?: 'BoostedMultiplier';
            numberOfPicks: number;
            numberOfBoostedPicks: number;
            multiplier: number;
            toWin: number;
        }>;
        maxNumberOfPicksForType?: Array<{
            __typename?: 'MaxPicksForType';
            type: Types.ProjectionType;
            maxPicks: number;
        }>;
        remaining: Array<{ __typename?: 'RemainingPickAmountType'; type: Types.ProjectionType; count: number }>;
        errors: Array<{
            __typename?: 'EntryError';
            code: number;
            key: string;
            type: Types.EntryErrorType;
            message: string;
            restrictedPlayerIdPairs?: Array<Array<string>>;
        }>;
        promotions: Array<{
            __typename?: 'EntryPromotionStatus';
            promotion: {
                __typename?: 'EntryPromotion';
                id: string;
                name: string;
                description: string;
                autoApply: boolean;
                type: Types.PromotionType;
                criteria: Array<{ __typename?: 'Criterion'; type: Types.CriterionType; value: string }>;
                metadata: Array<{ __typename?: 'DataField'; type: Types.MetadataType; value: string }>;
            };
            errors?: Array<{
                __typename?: 'EntryPromotionError';
                type?: Types.CriterionType;
                title: string;
                shortDescription: string;
                longDescription: string;
                order: number;
            }>;
        }>;
    }>;
};

export type EntryValidationResultFragment = {
    __typename?: 'EntryValidationOutput';
    entryRulesId?: string;
    currency: Types.Currency;
    amount: number;
    status: Types.EntryValidationStatus;
    toWin: number;
    p2pGuaranteedPrize?: number;
    p2pGuaranteedPrizeAmount?: number;
    perfectRegularMultiplier?: number;
    perfectBoostedMultiplier?: number;
    minMysteryMultiplier?: number;
    maxMysteryMultiplier?: number;
    mysteryMultipliers?: Array<number>;
    gameMode?: Types.GameMode;
    maxAmount: number;
    minAmount: number;
    minNumberOfTeams: number;
    minNumberOfPicks: number;
    maxNumberOfPicks: number;
    edgeCombos?: Array<Array<string>>;
    dynamicMultipliers?: Array<{
        __typename?: 'DynamicMultiplier';
        multiplier: number;
        numberOfPicks: number;
        winningPicks: number;
        toWin: number;
    }>;
    boostedMultipliers?: Array<{
        __typename?: 'BoostedMultiplier';
        numberOfPicks: number;
        numberOfBoostedPicks: number;
        multiplier: number;
        toWin: number;
    }>;
    maxNumberOfPicksForType?: Array<{ __typename?: 'MaxPicksForType'; type: Types.ProjectionType; maxPicks: number }>;
    remaining: Array<{ __typename?: 'RemainingPickAmountType'; type: Types.ProjectionType; count: number }>;
    errors: Array<{
        __typename?: 'EntryError';
        code: number;
        key: string;
        type: Types.EntryErrorType;
        message: string;
        restrictedPlayerIdPairs?: Array<Array<string>>;
    }>;
    promotions: Array<{
        __typename?: 'EntryPromotionStatus';
        promotion: {
            __typename?: 'EntryPromotion';
            id: string;
            name: string;
            description: string;
            autoApply: boolean;
            type: Types.PromotionType;
            criteria: Array<{ __typename?: 'Criterion'; type: Types.CriterionType; value: string }>;
            metadata: Array<{ __typename?: 'DataField'; type: Types.MetadataType; value: string }>;
        };
        errors?: Array<{
            __typename?: 'EntryPromotionError';
            type?: Types.CriterionType;
            title: string;
            shortDescription: string;
            longDescription: string;
            order: number;
        }>;
    }>;
};

export type RemainingPicksForTypeFragment = {
    __typename?: 'RemainingPickAmountType';
    type: Types.ProjectionType;
    count: number;
};

export type GetMysteryMultipliersFrequencyQueryVariables = Types.Exact<{ [key: string]: never }>;

export type GetMysteryMultipliersFrequencyQuery = {
    __typename?: 'Query';
    getMysteryMultipliersFrequency: {
        __typename?: 'MysteryMultipliers';
        totalWeight: number;
        multipliersFrequency: Array<{
            __typename?: 'MysteryMultiplierFrequency';
            multiplier: number;
            frequency: number;
            percentage: number;
        }>;
    };
};

export const RemainingPicksForTypeFragmentDoc = gql`
    fragment RemainingPicksForType on RemainingPickAmountType {
        type
        count
    }
`;
export const EntryValidationResultFragmentDoc = gql`
    fragment EntryValidationResult on EntryValidationOutput {
        entryRulesId
        currency
        amount
        status
        toWin
        p2pGuaranteedPrize
        p2pGuaranteedPrizeAmount
        perfectRegularMultiplier
        perfectBoostedMultiplier
        minMysteryMultiplier
        maxMysteryMultiplier
        mysteryMultipliers
        gameMode
        dynamicMultipliers {
            multiplier
            numberOfPicks
            winningPicks
            toWin
        }
        boostedMultipliers {
            ...BoostedMultipliers
        }
        maxAmount
        minAmount
        minNumberOfTeams
        minNumberOfPicks
        maxNumberOfPicks
        maxNumberOfPicksForType {
            ...MaxPicksPerType
        }
        remaining {
            ...RemainingPicksForType
        }
        errors {
            code
            key
            type
            message
            restrictedPlayerIdPairs
        }
        promotions {
            promotion {
                ...EntryPromotionDetails
            }
            errors {
                type
                title
                shortDescription
                longDescription
                order
            }
        }
        edgeCombos
    }
    ${BoostedMultipliersFragmentDoc}
    ${MaxPicksPerTypeFragmentDoc}
    ${RemainingPicksForTypeFragmentDoc}
    ${EntryPromotionDetailsFragmentDoc}
`;
export const ValidateEntryDocument = gql`
    query ValidateEntry($entry: EntryValidation!) {
        validateEntry(entryValidation: $entry) {
            ...EntryValidationResult
        }
    }
    ${EntryValidationResultFragmentDoc}
`;

export function useValidateEntryQuery(options: Omit<Urql.UseQueryArgs<ValidateEntryQueryVariables>, 'query'>) {
    return Urql.useQuery<ValidateEntryQuery, ValidateEntryQueryVariables>({ query: ValidateEntryDocument, ...options });
}
export const ValidateEntryWithRulesDocument = gql`
    query ValidateEntryWithRules($rulesIds: [String!]!, $entry: EntryValidation!) {
        validateEntryOnEntryRules(entryRulesIds: $rulesIds, entryValidation: $entry) {
            ...EntryValidationResult
        }
    }
    ${EntryValidationResultFragmentDoc}
`;

export function useValidateEntryWithRulesQuery(
    options: Omit<Urql.UseQueryArgs<ValidateEntryWithRulesQueryVariables>, 'query'>
) {
    return Urql.useQuery<ValidateEntryWithRulesQuery, ValidateEntryWithRulesQueryVariables>({
        query: ValidateEntryWithRulesDocument,
        ...options,
    });
}
export const GetMysteryMultipliersFrequencyDocument = gql`
    query GetMysteryMultipliersFrequency {
        getMysteryMultipliersFrequency {
            totalWeight
            multipliersFrequency {
                multiplier
                frequency
                percentage
            }
        }
    }
`;

export function useGetMysteryMultipliersFrequencyQuery(
    options?: Omit<Urql.UseQueryArgs<GetMysteryMultipliersFrequencyQueryVariables>, 'query'>
) {
    return Urql.useQuery<GetMysteryMultipliersFrequencyQuery, GetMysteryMultipliersFrequencyQueryVariables>({
        query: GetMysteryMultipliersFrequencyDocument,
        ...options,
    });
}
